import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { GO_KEYS, gnavOn } from '../lib/ux-prefs.ts';
import { api, describeError, type Presence } from '../lib/api.ts';
import { T, tx, UI, LANGS } from '../lib/i18n.ts';
import { ago } from '../lib/format.ts';
import { loginHref } from '../lib/guest.ts';
import { PAGES, GROUPS, PAGE_ICON, CUSTOM_PAGES, TAB_DEFAULTS, settingCountOf } from '../lib/pages.ts';
import { useApp } from '../state/app.tsx';
import { useLayout } from '../state/layout.tsx';
import { isNavHidden, navOrder, tabIds } from '@jyfk/settings/layout';
import { withLock, withTip } from '../lib/layout-ops.ts';
import { useToast } from './Toast.tsx';
import { IX, INBOX_CHANGED, fill } from '../lib/inbox-copy.ts';
import { Icon } from './Icon.tsx';
import { InPageAlerts } from './InPageAlerts.tsx';

/**
 * 外框：品牌条、顶栏、侧栏、状态栏、手机底栏。
 *
 * 结构和类名照原型（`.app` 的 grid 已经把五块的位置排好了）。
 *
 * ── 和原型的区别 ──────────────────────────────────────
 * 状态栏上的数字**全是真的**。原型那一行写死了「GMGN 118ms / 额度 11% /
 * 排队 2」，那是给设计看的。这里只显示我们确实知道的：
 * 扩展在不在线、最后一次心跳多久以前、在座多久。
 *
 * 不知道的就不显示。一个风控工具最危险的失败不是「不工作」，
 * 而是「看起来在工作」—— 用户瞥一眼状态栏以为一切正常。
 */

const LB = {
  lockedBar: T('布局已锁定，板块拖不动（防止盯盘时手滑拖乱）', '佈局已鎖定，板塊拖不動（防止盯盤時手滑拖亂）', 'Layout is locked so blocks cannot be dragged'),
  unlock: T('解锁，我要调整', '解鎖，我要調整', 'Unlock'),
  tip: T('这一页每一块都能挪：鼠标移到一块上，标题栏右边会出现手柄，按住拖。也能折叠、隐藏、切半宽 / 整行。键盘：Tab 到手柄，按空格拿起、方向键挪。',
    '這一頁每一塊都能挪：滑鼠移到一塊上，標題欄右邊會出現手柄，按住拖。也能摺疊、隱藏、切半寬 / 整行。鍵盤：Tab 到手柄，按空格拿起、方向鍵挪。',
    'Every block here can be moved: hover it, grab the handle in its header. You can also collapse, hide or switch width. Keyboard: Tab to the handle, Space to pick up, arrows to move.'),
  lock: T('锁定布局', '鎖定佈局', 'Lock layout'),
  gotIt: T('知道了', '知道了', 'Got it'),
  toggleOn: T('板块现在可以拖。点一下锁定，防止盯盘时拖乱', '板塊現在可以拖。點一下鎖定，防止盯盤時拖亂', 'Blocks are draggable — click to lock'),
  toggleOff: T('布局已锁定，点一下解锁', '佈局已鎖定，點一下解鎖', 'Layout locked — click to unlock'),
  lockedToast: T('布局已锁定', '佈局已鎖定', 'Layout locked'),
  unlockedToast: T('已解锁，可以拖了', '已解鎖，可以拖了', 'Unlocked')
};

export function Shell({ children }: { children: React.ReactNode }): React.ReactElement {
  const app = useApp();
  const lang = app.lang;
  const loc = useLocation();
  const current = loc.pathname.startsWith('/p/') ? loc.pathname.slice(3) : '';
  useGoKeys();

  return (
    <div className="app">
      {/* 点品牌回首页。游客回的是产品介绍，登录了的人 `/` 会直接跳总览 */}
      <Link className="brandbar" to="/" aria-label={tx(UI.appName, lang)}>
        {/* logo 固定用描边：它本身已经有一个色块底，再填实心就糊成一团认不出盾牌 */}
        <span className="logo"><Icon name="shield" style="line" /></span>
        <span className="nm">{tx(UI.appName, lang)}</span>
      </Link>

      <TopBar current={current} />
      <SideBar current={current} />

      <main className="main">
        <div className="page">
          {app.guest ? <GuestBar /> : null}
          {/* 有可拖板块的页面才说「每一块都能挪」（还没做的那几页整页是一段说明，没东西可挪） */}
          {current !== '' && !CUSTOM_PAGES.has(current) ? <LayoutBar /> : null}
          {children}
        </div>
      </main>

      <StatusBar />
      <TabBar current={current} />
      {/* 网页开着时的页面内提醒和熔断锁（lib/inpage-alerts.ts） */}
      <InPageAlerts />
    </div>
  );
}

/**
 * 先按 G、1.2 秒内再按一个字母，跳到那一页（通用设置 ux_hk + ux_gnav，和原型 palette.js 一样）。
 * 在输入框里打字、按着 Ctrl / Cmd / Alt 时不响应；按下 G 之后的那个键不再往下传（免得收件箱的单键同时生效）
 */
function useGoKeys(): void {
  const app = useApp();
  const navigate = useNavigate();
  const on = gnavOn(app.settings);
  useEffect(() => {
    if (!on) return;
    let pending = 0;
    const onKey = (e: KeyboardEvent): void => {
      if (e.ctrlKey || e.metaKey || e.altKey || e.repeat) return;
      const t = e.target as HTMLElement | null;
      if (t !== null && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
      const k = e.key.toLowerCase();
      if (pending !== 0 && Date.now() - pending < 1200) {
        pending = 0;
        const to = GO_KEYS[k];
        if (to) { e.preventDefault(); e.stopImmediatePropagation(); navigate('/p/' + to); }
        return;
      }
      if (k === 'g') { pending = Date.now(); e.stopImmediatePropagation(); }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [on, navigate]);
}

/** 手机底栏：用户在外观页「导航」里挑的 5 个，不满用默认的补（见 @jyfk/settings/layout 的 tabIds） */
function TabBar({ current }: { current: string }): React.ReactElement {
  const app = useApp();
  const lang = app.lang;
  const nav = useLayout().doc.nav;
  const ids = tabIds(nav, TAB_DEFAULTS, PAGES.map(p => p.id));
  return (
      <nav className="tabbar">
        {ids.map(id => {
          const p = PAGES.find(x => x.id === id);
          if (!p) return null;
          return (
            <Link key={id} to={'/p/' + id} {...(current === id ? { 'aria-current': 'page' as const } : {})}>
              <Icon name={PAGE_ICON[id] ?? 'cog'} />
              <span>{tx(p.title, lang)}</span>
            </Link>
          );
        })}
      </nav>
  );
}

/**
 * 每一页顶上那条布局提示（照原型的 layoutBar）。两种情况：
 *   锁着：常显一条，说清「现在拖不动是因为你锁了」，一键解锁 —— 不然过两周自己都想不起来为什么拖不动
 *   没锁：前三次打开各提示一次「每一块都能挪」，点「知道了」就不再出现 ——
 *        原型上一版把拖动藏在一个编辑模式按钮后面，用户根本不知道有这功能
 * 手机上不显示（手机不拖，见 DragZone）。
 */
function LayoutBar(): React.ReactElement | null {
  const app = useApp();
  const lang = app.lang;
  const Lx = useLayout();
  if (!Lx.ready) return null;
  if (Lx.doc.lock) {
    return (
      <div className="layoutbar lock hide-m" role="status">
        <span className="ic"><Icon name="lock" /></span>
        <span>{tx(LB.lockedBar, lang)}</span>
        <span className="sp" />
        <button type="button" className="btn sm primary" onClick={() => Lx.change(d => withLock(d, false))}>
          <Icon name="layout" cls="sm" />{tx(LB.unlock, lang)}
        </button>
      </div>
    );
  }
  if (Lx.doc.tip > 3) return null;
  return (
    <div className="layoutbar hide-m" role="status">
      <span className="ic"><Icon name="layout" /></span>
      <span>{tx(LB.tip, lang)}</span>
      <span className="sp" />
      <button type="button" className="btn sm" onClick={() => Lx.change(d => withLock(d, true))}>
        <Icon name="lock" cls="sm" />{tx(LB.lock, lang)}
      </button>
      <button type="button" className="btn sm primary" onClick={() => Lx.change(d => withTip(d, 99))}>
        <Icon name="check" cls="sm" />{tx(LB.gotIt, lang)}
      </button>
    </div>
  );
}

/**
 * 游客模式下每一页顶上那一条。
 *
 * 不做成吸顶：吸顶的条子在每一页都吃掉一截屏幕，而顶栏右边
 * 已经常驻一个「免费注册 / 登录」按钮了。这一条只负责把话说清楚一次：
 * 你在看什么、改了会怎样、登录之后多了什么。
 */
function GuestBar(): React.ReactElement {
  const app = useApp();
  const loc = useLocation();
  const lang = app.lang;
  return (
    <div className="banner info guestbar" role="note">
      <span className="ic"><Icon name="eye" /></span>
      <span>
        <b>{tx(UI.guestBar, lang)}</b>
        <div className="hint" style={{ marginTop: 3 }}>{tx(UI.guestBarWhy, lang)}</div>
      </span>
      <span className="b-act">
        <Link className="btn sm primary" to={loginHref(loc.pathname + loc.search)}>
          {tx(UI.guestCta, lang)}
        </Link>
      </span>
    </div>
  );
}

function TopBar({ current }: { current: string }): React.ReactElement {
  const app = useApp();
  const toast = useToast();
  const loc = useLocation();
  const lang = app.lang;
  const [langOpen, setLangOpen] = useState(false);
  const [langBusy, setLangBusy] = useState(false);
  const page = PAGES.find(p => p.id === current);

  /**
   * 换语言。**这一项是即时落库的**（见 state/app.tsx 的 setLang），
   * 所以要处理失败 —— 静默失败的表现是「点了菜单关掉了，语言没变」，
   * 用户只会再点一次，然后又一次。
   */
  const pickLang = async (id: (typeof LANGS)[number][0]): Promise<void> => {
    setLangBusy(true);
    const err = await app.setLang(id);
    setLangBusy(false);
    setLangOpen(false);
    if (!err) return;
    toast.fail(
      err.kind === 'conflict'
        ? '刚才别处也在改设置，已经把最新的读回来了 —— 再点一次语言'
        : describeError(err)
    );
  };

  return (
    <header className="topbar">
      <div className="crumb">
        {tx(UI.tagline, lang)} / <b>{page ? tx(page.title, lang) : tx(UI.appName, lang)}</b>
      </div>
      <div className="top-right">
        <div style={{ position: 'relative' }}>
          <button
            type="button"
            className="iconbtn"
            aria-haspopup="menu"
            aria-expanded={langOpen}
            title={tx(UI.language, lang)}
            onClick={() => setLangOpen(v => !v)}
          >
            <Icon name="globe" />
          </button>
          {langOpen ? (
            <>
              {/* 点外面关掉。用一层透明的接盘层而不是 document 上挂 listener ——
                  后者要自己处理「点的是按钮本身」那个反复开关的情况 */}
              <div
                style={{ position: 'fixed', inset: 0, zIndex: 39 }}
                onClick={() => setLangOpen(false)}
              />
              <div className="menu" role="menu" style={{ position: 'absolute', right: 0, top: '100%', zIndex: 40 }}>
                {LANGS.map(([id, name]) => (
                  <button
                    key={id}
                    type="button"
                    role="menuitemradio"
                    aria-checked={lang === id}
                    disabled={langBusy}
                    onClick={() => void pickLang(id)}
                  >
                    {name}
                    {lang === id ? <Icon name="check" cls="sm ck" /> : null}
                  </button>
                ))}
              </div>
            </>
          ) : null}
        </div>
        <InboxBell />
        <LayoutToggle />
        <Link className="iconbtn" to="/p/look" title={tx(UI.appearance, lang)}>
          <Icon name="palette" />
        </Link>
        {app.guest ? (
          /* 游客没有账号可看，这个位置换成注册入口 —— 每一页都点得到 */
          <Link className="btn sm primary" to={loginHref(loc.pathname + loc.search)}>
            {tx(UI.guestCta, lang)}
          </Link>
        ) : (
          <Link className="iconbtn" to="/account" title={tx(UI.account, lang)}>
            <Icon name="user" />
          </Link>
        )}
      </div>
    </header>
  );
}

/**
 * 顶栏的铃铛：收件箱里最近 7 天还没处理的提醒有几条（按提醒算，不按通道）。
 * 一分钟问一次服务器；在收件箱里处理了一条之后马上重数（INBOX_CHANGED）。游客没有收件箱，只画一个入口
 */
function InboxBell(): React.ReactElement {
  const app = useApp();
  const lang = app.lang;
  const [n, setN] = useState<{ count: number; capped: boolean } | null>(null);
  useEffect(() => {
    if (app.guest) { setN(null); return; }
    let alive = true;
    const pull = async (): Promise<void> => {
      const r = await api.get<{ count: number; capped: boolean }>('/inbox/unread');
      if (alive && r.ok) setN(r.data);
    };
    void pull();
    const t = setInterval(() => void pull(), 60_000);
    const on = (): void => { void pull(); };
    window.addEventListener(INBOX_CHANGED, on);
    return () => { alive = false; clearInterval(t); window.removeEventListener(INBOX_CHANGED, on); };
  }, [app.guest]);
  const count = n?.count ?? 0;
  const shown = n?.capped === true || count > 99 ? '99+' : String(count);
  const title = count > 0 ? fill(tx(IX.bell, lang), { n: shown }) : tx(IX.bellNone, lang);
  return (
    <Link className="iconbtn" to="/p/inbox" title={title} aria-label={title}>
      <Icon name="bell" />
      {count > 0 ? <span className="n" style={{ padding: '0 3px' }}>{shown}</span> : null}
    </Link>
  );
}

/** 顶栏的锁：锁上之后所有页面的板块都拖不动（照原型的 layoutToggle）。手机上不显示 */
function LayoutToggle(): React.ReactElement {
  const app = useApp();
  const lang = app.lang;
  const toast = useToast();
  const Lx = useLayout();
  const locked = Lx.doc.lock;
  return (
    <button
      type="button"
      className={locked ? 'iconbtn on' : 'iconbtn'}
      data-act="layoutToggle"
      aria-pressed={locked}
      title={tx(locked ? LB.toggleOff : LB.toggleOn, lang)}
      aria-label={tx(locked ? LB.toggleOff : LB.toggleOn, lang)}
      onClick={() => {
        Lx.change(d => withLock(d, !d.lock));
        toast.show(tx(locked ? LB.unlockedToast : LB.lockedToast, lang), { kind: 'ok' });
      }}
    >
      <Icon name={locked ? 'lock' : 'layout'} />
    </button>
  );
}

function SideBar({ current }: { current: string }): React.ReactElement {
  const app = useApp();
  const lang = app.lang;
  /* 顺序和隐藏照外观页「导航」里排的。分组还是那几组，组内按用户的顺序 */
  const nav = useLayout().doc.nav;
  const order = navOrder(nav, PAGES.map(p => p.id));
  const pages = order
    .map(id => PAGES.find(p => p.id === id)!)
    .filter(p => p !== undefined && (!isNavHidden(nav, p.id) || p.id === current));

  return (
    <nav className="side" aria-label="main">
      {GROUPS.map(g => {
        const items = pages.filter(p => p.grp === g.id);
        if (items.length === 0) return null;
        return (
          <div key={g.id}>
            <div className="grp">{tx(g.l, lang)}</div>
            {items.map(p => {
              const n = settingCountOf(p.id);
              return (
                <Link
                  key={p.id}
                  to={'/p/' + p.id}
                  {...(current === p.id ? { 'aria-current': 'page' as const } : {})}
                >
                  <Icon name={PAGE_ICON[p.id] ?? 'cog'} />
                  <span className="grow ell">{tx(p.title, lang)}</span>
                  {/* 还没做的页面直接标出来，不让人点进去才发现。
                      有配置项的标项数，方便估「这一页要改多少东西」 */}
                  {CUSTOM_PAGES.has(p.id)
                    ? <span className="n plain">—</span>
                    : n > 0 ? <span className="n plain">{n}</span> : null}
                </Link>
              );
            })}
          </div>
        );
      })}
      {/* 免费的模拟交易扩展（2026-10-30：登录之后侧栏里也找得到，以前只在首页页脚） */}
      <div>
        <div className="grp">{lang === 'en' ? 'Tools' : lang === 'zh-HK' ? '工具' : '工具'}</div>
        <Link to="/docs"><Icon name="note" /><span className="grow ell">{lang === 'en' ? 'User guide' : lang === 'zh-HK' ? '使用教程' : '使用教程'}</span></Link>
        <Link to="/paper"><Icon name="chart" /><span className="grow ell">{lang === 'en' ? 'Paper trading' : lang === 'zh-HK' ? '模擬交易' : '模拟交易'}</span></Link>
        <Link to="/gmgn-key"><Icon name="key" /><span className="grow ell">{lang === 'en' ? 'GMGN API key guide' : lang === 'zh-HK' ? 'GMGN Key 教程' : 'GMGN Key 教程'}</span></Link>
        {/* 常用平台（老板的邀请链接，页面顶上写明返佣）。放在「工具」里，不放进提醒、熔断这些地方（见 lib/partners-copy.ts） */}
        <Link to="/partners"><Icon name="link" /><span className="grow ell">{lang === 'en' ? 'Platforms' : lang === 'zh-HK' ? '常用平台' : '常用平台'}</span></Link>
      </div>
    </nav>
  );
}

/**
 * 状态栏。三十秒问一次在座状态。
 *
 * 为什么是三十秒：扩展每 5 分钟报一次到，8 分钟没报到算失联（见 @jyfk/scheduler 的 EXTENSION_ALIVE_MS）。
 * 三十秒问一次够及时；问得太疏的话，
 * 「扩展掉线了」这件事会晚很久才显示出来 —— 而那正是最该早知道的事。
 */
function StatusBar(): React.ReactElement {
  const app = useApp();
  /* 游客没有扩展、没有在座状态可问。显示「连不上服务器」那种话是在撒谎 ——
     服务器好好的，只是他还没登录 */
  if (app.guest) {
    return (
      <footer className="statusbar">
        <span className="it"><i className="led sm off" />{tx(UI.guestStatus, app.lang)}</span>
      </footer>
    );
  }
  return <MemberStatusBar />;
}

function MemberStatusBar(): React.ReactElement {
  const app = useApp();
  const lang = app.lang;
  const [presence, setPresence] = useState<Presence | null>(null);
  const [now, setNow] = useState(() => Date.now());
  /* null = 还没问过，false = 问过但失败了。两个要分开 ——
     「不知道」和「确实离线」显示的话不一样 */
  const [reachable, setReachable] = useState<boolean | null>(null);

  useEffect(() => {
    let live = true;
    const tick = async (): Promise<void> => {
      const r = await api.get<Presence>('/presence');
      if (!live) return;
      setNow(Date.now());
      if (r.ok) {
        setPresence(r.data);
        setReachable(true);
      } else {
        setReachable(false);
      }
    };
    void tick();
    const t = setInterval(() => void tick(), 30_000);
    /* 「N 分钟前」要自己走起来，不然它会停在最后一次请求那一刻 */
    const t2 = setInterval(() => setNow(Date.now()), 10_000);
    return () => {
      live = false;
      clearInterval(t);
      clearInterval(t2);
    };
  }, []);

  const extState = reachable === null
    ? 'unknown'
    : reachable === false
      ? 'offline-server'
      : presence?.extensionAlive
        ? 'alive'
        : presence?.lastBeatAt
          ? 'stale'
          : 'never';

  return (
    <footer className="statusbar">
      <span className="it">
        <i className={
          extState === 'alive' ? 'led sm' : extState === 'stale' ? 'led sm warn' : 'led sm off'
        } />
        {extState === 'alive' ? tx(UI.extOnDuty, lang)
          : extState === 'stale' ? tx(UI.extOffline, lang)
            : extState === 'never' ? tx(UI.extNever, lang)
              : extState === 'offline-server' ? tx(UI.serverUnreachable, lang)
                : tx(UI.loading, lang)}
      </span>

      {presence ? (
        <span className="it">
          {presence.sitting
            ? `${tx(UI.sitting, lang)} · ${Math.floor(presence.sittingSeconds / 60)} 分钟`
            : tx(UI.notSitting, lang)}
        </span>
      ) : null}

      <span className="sp" />

      <span className={extState === 'stale' ? 'it stale' : 'it'}>
        {tx(UI.lastSync, lang)} {ago(presence?.lastBeatAt ?? null, now, lang)}
      </span>
      {presence?.clientVersion ? <span className="it">扩展 {presence.clientVersion}</span> : null}
    </footer>
  );
}
